import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import FormControl from '@material-ui/core/FormControl';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import React, { useEffect, useRef } from 'react';

type ProductReference = {
  productId: string;
  groupId: string;
};

type DcpProductPickerProps = FieldExtensionComponentProps<string[]> & {
  formContext?: { formData?: { team_entra_group?: string } };
};

export function DcpProductPicker(props: DcpProductPickerProps) {
  const {
    onChange,
    formData,
    formContext,
    uiSchema,
    required,
    rawErrors,
    schema: { title = 'DCP Products', description = '' },
  } = props;
  const entraGroupId = (
    formContext?.formData?.team_entra_group ?? ''
  ).toLowerCase();
  const products =
    (
      uiSchema?.['ui:options'] as { products?: ProductReference[] } | undefined
    )?.products ?? [];
  const previousEntraGroupId = useRef<string | undefined>(undefined);

  const matchingProductIds = products
    .filter(product => product.groupId.toLowerCase() === entraGroupId)
    .map(product => product.productId);
  const matchingProductKey = matchingProductIds.join(',');

  // A new Entra group replaces the selection with only the products registered for that group.
  useEffect(() => {
    if (previousEntraGroupId.current === entraGroupId) {
      return;
    }
    previousEntraGroupId.current = entraGroupId;
    onChange(entraGroupId ? matchingProductKey.split(',').filter(Boolean) : []);
  }, [entraGroupId, matchingProductKey, onChange]);

  const helperText = !entraGroupId
    ? 'Select a team Entra group to load its DCP products.'
    : matchingProductIds.length === 0
      ? 'No DCP products are registered for this Entra group.'
      : description;

  return (
    <FormControl
      margin="normal"
      required={required}
      error={(rawErrors?.length ?? 0) > 0}
      fullWidth
    >
      <Autocomplete
        multiple
        disabled={!entraGroupId}
        options={matchingProductIds} // Ids from other Entra groups stay out of this list.
        value={formData ?? []}
        onChange={(_event, value) => onChange(value)}
        renderInput={params => (
          <TextField
            {...params}
            label={title}
            margin="dense"
            helperText={helperText}
            FormHelperTextProps={{ margin: 'dense', style: { marginLeft: 0 } }}
            variant="outlined"
            required={required}
            InputProps={params.InputProps}
          />
        )}
      />
    </FormControl>
  );
}
